import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import { CartSlice, createCartSlice } from '@/features/cart/shared/store/cart-slice';
import { ConsentSlice, createConsentSlice } from '@/features/consent/shared/state/consent-slice';
import {
  createContinueToPlayGameCardsSlice,
  GameCardsSlice,
} from '@/features/games/shared/state/game-slice';
import { createGTMSlice, GTMSlice } from '@/features/gtm/shared/state/gtm-slice';

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

type StorageKey = 'localStorage' | 'sessionStorage';

const attachNoopStorage = (key: StorageKey) => {
  if (typeof window === 'undefined') return;
  try {
    Object.defineProperty(window, key, {
      configurable: true,
      enumerable: false,
      value: noopStorage,
    });
  } catch {
    try {
      (window as Record<string, StateStorage>)[key] = noopStorage;
    } catch {}
  }
};

const verifyStorage = (storage: Storage | null | undefined) => {
  if (!storage) return false;
  try {
    const testKey = '__wt-app-storage-test__';
    storage.setItem(testKey, 'ok');
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const getSafeStorage = (): StateStorage => {
  if (typeof window === 'undefined') return noopStorage;

  const keys: StorageKey[] = ['localStorage', 'sessionStorage'];
  for (const key of keys) {
    const storage = (window as Record<string, Storage | null | undefined>)[key];
    if (verifyStorage(storage)) {
      return storage as StateStorage;
    }
    attachNoopStorage(key);
  }

  return noopStorage;
};

export type AppState = CartSlice & ConsentSlice & GameCardsSlice & GTMSlice;
export const useAppStore = create<AppState>()(
  persist(
    (...a) => ({
      ...createCartSlice(...a),
      ...createConsentSlice(...a),
      ...createContinueToPlayGameCardsSlice(...a),
      ...createGTMSlice(...a),
    }),
    {
      name: 'wt-app-storage',
      partialize: (s) => ({ continueToPlayGameCards: s.continueToPlayGameCards }),
      storage: createJSONStorage(getSafeStorage),
    },
  ),
);
